<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="mb-0">BDM Performance — Lifetime</h5>

      <div class="d-flex align-items-center" style="gap: 8px">
        <b-form-input
          type="text"
          placeholder="Search BDM..."
          v-model="search"
          size="sm"
        />
      </div>
    </div>

    <!-- Table -->
    <b-table
      :key="key"
      responsive
      :fields="fields"
      :items="items"
      :busy="loading"
      thead-class="text-nowrap"
      show-empty
      class="mt-1"
    >
      <template #cell(bdm)="{ item }">
        <span class="font-weight-bold">{{ item.username }}</span>
      </template>
      <template #cell(customers)="{ item }">
        <span>{{ item.totalCustomer || 0 }} </span>
      </template>
      <template #cell(actives)="{ item }">
        <span>{{ item.totalActiveCustomer || 0 }}</span>
      </template>
      <template #cell(deposits)="{ item }">
        <span>{{
          formatCurrencyBuiltInFunc(item.totalDepositMatch || 0)
        }}</span>
      </template>
      <template #cell(withdrawals)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.withdrawals || 0) }}</span>
      </template>
      <template #cell(turnover)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.turnover || 0) }}</span>
      </template>
      <template #cell(bonus)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.totalBonusCash || 0) }}</span>
      </template>
      <template #cell(clientResult)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.clientResult || 0) }}</span>
      </template>
      <template #cell(adjustments)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.adjustments || 0) }}</span>
      </template>
      <template #cell(productFees)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.productFees || 0) }}</span>
      </template>
      <template #cell(gst)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.gst || 0) }}</span>
      </template>
      <template #cell(poc)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.poc || 0) }}</span>
      </template>
      <template #cell(bankFees)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.bankFees || 0) }}</span>
      </template>
      <template #cell(adminFees)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.adminFees || 0) }}</span>
      </template>
      <template #cell(clientValue)="{ item }">
        <span>{{ formatCurrencyBuiltInFunc(item.clientValue || 0) }}</span>
      </template>
      <template #cell(comm)="{ item }">
        <span>{{ item.comm || 0 }}%</span>
      </template>

      <template #cell(commission)="{ item }">
        <span> {{ formatCurrencyBuiltInFunc(item.commission || 0) }}</span>
      </template>
    </b-table>

    <div
      v-if="pagination.total > 0"
      class="d-flex align-items-center justify-content-end"
    >
      <b-pagination
        v-model="pagination.page"
        :total-rows="pagination.total"
        :per-page="pagination.limit"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
      >
        <template #prev-text>
          <feather-icon icon="ChevronLeftIcon" size="18" />
        </template>
        <template #next-text>
          <feather-icon icon="ChevronRightIcon" size="18" />
        </template>
      </b-pagination>
    </div>
  </div>
</template>
<script>
import {
  BRow,
  BCol,
  BCard,
  BFormSelect,
  BFormInput,
  BButton,
  BTable,
  BLink,
  BPagination,
  BFormGroup,
} from "bootstrap-vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import { mapActions } from "vuex";
import useToastHandler from "@/services/toastHandler";
import { debounce } from "debounce";

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BFormSelect,
    BFormInput,
    BButton,
    BTable,
    BLink,
    BPagination,
    BFormGroup,
  },
  data() {
    return {
      key: 0,
      loading: false,
      fields: [
        { key: "bdm", label: "BDM Name" },
        { key: "customers", label: "Customers" },
        { key: "actives", label: "Active Clients" },
        { key: "deposits", label: "Total Deposits" },
        { key: "withdrawals", label: "Total Withdrawals" },
        { key: "turnover", label: "Turnover" },
        { key: "bonus", label: "Bonuses" },
        { key: "clientResult", label: "Client Result" },
        { key: "adjustments", label: "Adjustments" },
        { key: "productFees", label: "Product Fees" },
        { key: "gst", label: "GST" },
        { key: "poc", label: "POC" },
        { key: "bankFees", label: "Bank Fees" },
        { key: "adminFees", label: "Admin Fees" },
        { key: "clientValue", label: "Client Value" },
        { key: "comm", label: "Comm %" },
        { key: "commission", label: "Commission Payable" },
      ],
      items: [],
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
      },
      search: "",
    };
  },
  async created() {
    await this.fetchAffiliates();
  },
  watch: {
    "pagination.page"() {
      this.getAffiliates();
    },
    search() {
      this.handleSearchDebounced();
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
  },
  methods: {
    ...mapActions("bdm/affiliates/list", {
      getAffiliates: "getAffiliates",
    }),
    formatCurrencyBuiltInFunc,
    async fetchAffiliates() {
      try {
        const { pagination, search } = this;
        this.loading = true;
        const { data, pagination: p } = await this.getAffiliates({
          page: pagination.page,
          limit: pagination.limit,
          bdmOnly: true,
          ...(search && { search: search }),
        });
        this.items = data;
        this.pagination = p;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.key++;
      this.fetchAffiliates();
    },
    formatName(item) {
      const { firstName, middleName, lastName } = item;
      const names = [firstName, middleName, lastName].filter(Boolean);
      return names.join(" ");
    },
  },
  setup() {
    const { showToastError, showToastSuccess } = useToastHandler();
    return {
      showToastError,
      showToastSuccess,
    };
  },
};
</script>
